/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { extend } from "@thd-nucleus/data-sources";
import {
  Alert, Button, Link,
  DrawerBody, DrawerFooter
} from '@one-thd/sui-atomic-components';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { SearchProducts } from './search-products/SearchProducts';
import { PreviouslyPurchased } from './previously-purchased/PreviouslyPurchased';
import { SearchResults } from './search-products/SearchResults';
import { CopyListTile } from '../addto/copy-list/CopyListTile';
import { AddToContext } from '../AddToContext';
import { 
  LocalizationBanners, 
  MyStoreButton,
  DeliveryZipcodeButton } from '@thd-olt-component-react/project-localization';
import { onLocalizationUpdate } from '../AddToHelpers';
import { SCREENS } from '../AddToConstants';

export const DefaultScreen = ({
  keyword,
  onSearch,
  handleClose,
  projectId,
  selectedGroup,
  selectedGroupId,
  products,
  selectedProject,
  updateList,
  customerType,
  setIsSearchResults,
  productPodItemData,
  projectDetails
}) => {

    const {
      MY_STORE,
      DELIVERY_ZIP
    } = SCREENS;

  const {
    successMessage,
    setSuccessMessage,
    showSuccessMessage,
    setShowSuccessMessage,
    showErrorMessage,
    setShowErrorMessage,
    addToType,
    setScreen
  } = useContext(AddToContext);

  const {
    projectName,
    preferredStore,
    assortmentZip,
    addresses
  } = projectDetails || {};
  const assortmentZipId = addresses?.find((address) => address.addressType === 'ASSORTMENT_ZIP')?.id || null;

  const { isConsumerApp } = useContext(ExperienceContext);
  const showLocalizationComponents = addToType === 'Project' && !isConsumerApp;

  const fsCopyListEnabled = useConfigService('fs:isCopyListEnabled');
  const successMessageRef = useRef(null);
  const errorMessageRef = useRef(null);
  const selectedProjectId = projectId || selectedProject?.projectId;
  const selectedProjectName = projectName || selectedProject?.projectName;
  const { searchKeyword } = productPodItemData || {};

  const handleClick = () => {
    handleClose();
    window.location.href = `/projects/${selectedProjectId}#group=${selectedGroupId.current}`;
  };

  const isMultipleProducts = products?.length > 1;

  const projectMessage = () => {
    const unassignedGroupMessage = (
      <>
        {isMultipleProducts ? 'Items' : 'Item'} successfully added to&nbsp;
        <Link href={`/projects/${selectedProjectId}`} className="sui-text-sm sui-underline">
          {selectedProjectName}
        </Link>&nbsp;
      </>
    );
    const projectMsg = (
      <>
        {isMultipleProducts ? 'Items' : 'Item'} successfully added to&nbsp;
        <Link href={`/projects/${selectedProjectId}`} className="sui-text-sm sui-underline">
          {selectedProjectName}
        </Link>&nbsp;
        in group&nbsp;
        <Link component="button" onClick={handleClick} className="sui-text-sm sui-underline">
          {selectedGroup}
        </Link>&nbsp;
      </>
    );
    return (
      selectedGroup === 'Unassigned' ? unassignedGroupMessage : projectMsg
    );
  };

  const listMessage = () => {
    return (
      <>
        Your item has been added to your list.
      </>
    );
  };

  const handleAlertClose = () => {
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
    setSuccessMessage('');
  };

  useEffect(() => {
    if (showSuccessMessage) {
      successMessageRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
    if (showErrorMessage) {
      errorMessageRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  }, [showSuccessMessage, showErrorMessage]);

  useEffect(() => {
    if (addToType === 'List') {
      window.LIFE_CYCLE_EVENT_BUS.trigger('add-to.addToMaterialsOverlay', {
        addType: 'list'
      });
    }
  }, []);
  const handleCloseText = !showSuccessMessage || !showErrorMessage ? 'close' : 'ok';

  return (
    <>
      <DrawerBody overflow="scroll">
        {
          successMessage?.length > 0 && (
            <Alert
              status="success"
              onClose={handleAlertClose}
              data-testid="add-to-success-alert"
              ref={successMessageRef}
            >
              {successMessage}
            </Alert>
          )
        }
        {showSuccessMessage && (
          <Alert
            status="success"
            onClose={handleAlertClose}
            data-testid="add-to-success-alert"
            ref={successMessageRef}
          >
            <div className="sui-flex sui-items-center">
              {addToType === 'Project' ? projectMessage() : listMessage()}
            </div>
          </Alert>
        )}
        {showErrorMessage && (
          <Alert
            status="error"
            onClose={() => setShowErrorMessage(false)}
            data-testid="add-to-error-alert"
            ref={errorMessageRef}
          >
            Apologies, a temporary technical issue occured. Please try again soon.
          </Alert>
        )}
        {!products?.length > 0 && (
          <>
            {showLocalizationComponents && (
              <LocalizationBanners 
                projectId={projectId}
                projectName={projectName}
                assortmentZip={assortmentZip}
                assortmentZipId={assortmentZipId}
                preferredStore={preferredStore}
                onLocalizationUpdate={onLocalizationUpdate}
                removeDrawerLocalStorageValues
              />
            )}
            <SearchProducts
              onSubmit={onSearch}
              setIsSearchResults={setIsSearchResults}
              searchKeyword={searchKeyword}
            />
            {showLocalizationComponents && (
              <div className='sui-flex sui-pt-4 sui-gap-2 sui-items-center sui-justify-end'>
                <DeliveryZipcodeButton
                  assortmentZipCode={assortmentZip}
                  onClick={() => {
                    setScreen(DELIVERY_ZIP);
                  }}
                />
                <MyStoreButton
                  storeId={preferredStore}
                  onClick={() => {
                    setScreen(MY_STORE);
                  }}
                />
              </div>
            )}
            {
              addToType === 'List' && keyword.length === 0 && fsCopyListEnabled && (
                <CopyListTile />
              )
            }
            {/* when fs is removed we will need to add back the addToType !== 'List' check */}
            {keyword.length === 0 && customerType === 'B2B' && !fsCopyListEnabled && (
              <PreviouslyPurchased
                updateList={updateList}
              />
            )}
            <SearchResults
              keyword={keyword}
              updateList={updateList}
              productPodItemData={productPodItemData}
            />
          </>
        )}
      </DrawerBody>
      <DrawerFooter position="center">
        <Button
          data-testid="add-to-cancel-button"
          variant={!showSuccessMessage && !showErrorMessage ? 'secondary' : 'primary'}
          fullWidth
          onClick={(__evt) => { handleClose(__evt, { type: handleCloseText }); }}
          type="submit"
        > {!showSuccessMessage && !showErrorMessage ? 'Cancel' : 'OK'}
        </Button>
      </DrawerFooter>
    </>
  );
};

DefaultScreen.propTypes = {
  keyword: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  projectName: PropTypes.string,
  projectId: PropTypes.string,
  selectedGroup: PropTypes.string,
  selectedGroupId: PropTypes.shape({
    current: PropTypes.string
  }),
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number
  })),
  selectedProject: PropTypes.shape({
    projectId: PropTypes.string,
    projectName: PropTypes.string
  }),
  updateList: PropTypes.func,
  customerType: PropTypes.string,
  setIsSearchResults: PropTypes.func,
  productPodItemData: PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    searchKeyword: PropTypes.string
  })
};

DefaultScreen.defaultProps = {
  selectedGroup: null,
  selectedGroupId: { current: '' },
  products: [],
  selectedProject: {},
  projectName: null,
  projectId: null,
  updateList: () => { },
  customerType: '',
  setIsSearchResults: () => { },
  productPodItemData: {}
};

DefaultScreen.dataModel = extend(
  SearchResults,
  PreviouslyPurchased,
  LocalizationBanners,
  MyStoreButton
);
